import React from "react";
import { Badge, Button, Text } from "@fluentui/react-components";
import {
  AddRegular,
  DeleteRegular,
  DismissRegular,
  DocumentArrowDownRegular,
} from "@fluentui/react-icons";
import type { ConversationRecord } from "../history";

interface HistoryPanelProps {
  conversations: ConversationRecord[];
  activeId: string;
  onResume: (id: string) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
  onClose: () => void;
  /** Triggers the local JSON download (archive.ts's exportHistoryAsJson). */
  onExport: () => void;
  /**
   * The open email's Purview sensitivity/retention label, read-only and
   * purely informational (no enforcement here — that lives in
   * data-classification.ts). `undefined`/`null` when none is available
   * (EWS-only connection, no label applied, or no email open) — the badge
   * is simply omitted in that case.
   */
  sensitivityLabel?: string | null;
}

function relativeDate(ts: number): string {
  return new Date(ts).toLocaleDateString();
}

export function HistoryPanel({
  conversations,
  activeId,
  onResume,
  onDelete,
  onNew,
  onClose,
  onExport,
  sensitivityLabel,
}: HistoryPanelProps) {
  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Text weight="semibold" size={400}>Conversations</Text>
        <Button appearance="subtle" icon={<DismissRegular />} aria-label="Close history" onClick={onClose} />
      </div>
      <Button appearance="primary" icon={<AddRegular />} onClick={onNew}>
        New conversation
      </Button>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Button appearance="secondary" icon={<DocumentArrowDownRegular />} onClick={onExport}>
          Export history
        </Button>
        {sensitivityLabel && (
          <Badge appearance="tint" color="informative" aria-label={`Current email label: ${sensitivityLabel}`}>
            {sensitivityLabel}
          </Badge>
        )}
      </div>
      <Text size={100} style={{ opacity: 0.6 }}>
        Downloads a JSON file of your conversation history stored on this device. No email content
        is included.
      </Text>
      {conversations.length === 0 ? (
        <Text size={200} style={{ opacity: 0.7 }}>No conversations yet.</Text>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, overflowY: "auto" }}>
          {conversations.map((c) => (
            <div
              key={c.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 8px",
                borderRadius: 4,
                background: c.id === activeId ? "rgba(0,0,0,0.06)" : "transparent",
              }}
            >
              <button
                type="button"
                onClick={() => onResume(c.id)}
                style={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  textAlign: "left",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <Text
                  block
                  style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {c.title}
                </Text>
                <Text size={100} style={{ opacity: 0.6 }}>{relativeDate(c.updatedAt)}</Text>
              </button>
              <Button
                appearance="subtle"
                size="small"
                icon={<DeleteRegular />}
                aria-label={`Delete ${c.title}`}
                onClick={() => onDelete(c.id)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
